import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import templatesService from '../../../../services/templates.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { showError } from '../../../../utils/ecxSwal';
import { ClinicInfoDetailItem, ClinicInfoPanel } from '../../settings/clinicsettings/info/clinicInfoParts';
import ClinicSettingsEmpty from '../../settings/clinicsettings/subparts/ClinicSettingsEmpty';
import ClinicSettingsListSkeleton from '../../settings/clinicsettings/subparts/ClinicSettingsListSkeleton';
import TemplateTypeList from '../TemplateTypeList';
import { formatReminderLabel, groupTemplatesByType } from '../templateTypeGroups';

const PLACEHOLDERS = ['PATIENT_NAME', 'DATE', 'TIME'];

const CallTemplates = () => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const [listLoading, setListLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState(false);
    const hasAutoSelected = useRef(false);
    const audioUrlRef = useRef(null);

    const content = selectedTemplate?.content || '';
    const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
    const characterCount = content.length;

    const revokeAudioUrl = useCallback(() => {
        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
        setAudioUrl(null);
    }, []);

    const loadTemplateById = useCallback(
        async (id) => {
            if (!id || !clinicId) return;

            setSelectedId(id);
            setDetailLoading(true);
            revokeAudioUrl();
            setAudioError(false);

            try {
                const res = await templatesService.getCallTemplateById(clinicId, id);
                setSelectedTemplate(res.data);
            } catch (err) {
                console.error(err);
                setSelectedTemplate(null);
                showError({
                    title: 'Load Failed',
                    text: 'Unable to load this call template. Please try again.',
                });
            } finally {
                setDetailLoading(false);
            }
        },
        [clinicId, revokeAudioUrl]
    );

    useEffect(() => {
        let cancelled = false;

        const loadAudio = async () => {
            if (!clinicId || !selectedTemplate?.id || !selectedTemplate?.audioPath) {
                revokeAudioUrl();
                setAudioError(false);
                return;
            }

            setAudioLoading(true);
            setAudioError(false);

            try {
                const response = await templatesService.getCallAudioPlay(
                    clinicId,
                    selectedTemplate.id
                );
                if (cancelled) return;

                const blob = new Blob([response.data], { type: 'audio/mpeg' });
                if (!blob.size) {
                    throw new Error('Empty audio response');
                }

                const url = URL.createObjectURL(blob);
                revokeAudioUrl();
                audioUrlRef.current = url;
                setAudioUrl(url);
            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    revokeAudioUrl();
                    setAudioError(true);
                }
            } finally {
                if (!cancelled) {
                    setAudioLoading(false);
                }
            }
        };

        loadAudio();

        return () => {
            cancelled = true;
        };
    }, [clinicId, selectedTemplate?.id, selectedTemplate?.audioPath, revokeAudioUrl]);

    useEffect(() => () => revokeAudioUrl(), [revokeAudioUrl]);

    const loadTemplates = useCallback(async () => {
        if (!clinicId) return;

        setListLoading(true);
        try {
            const res = await templatesService.getCallTemplates(clinicId);
            const items = res.data || [];
            setTemplates(items);

            if (items.length > 0 && !hasAutoSelected.current) {
                hasAutoSelected.current = true;
                const firstTemplate = groupTemplatesByType(items)[0]?.templates[0];
                if (firstTemplate) {
                    await loadTemplateById(firstTemplate.id);
                }
            }
        } catch (err) {
            console.error(err);
            setTemplates([]);
            showError({
                title: 'Load Failed',
                text: 'Unable to load call templates. Please try again.',
            });
        } finally {
            setListLoading(false);
        }
    }, [clinicId, loadTemplateById]);

    useEffect(() => {
        hasAutoSelected.current = false;
        loadTemplates();
    }, [loadTemplates]);

    const isActive = useMemo(
        () => Boolean(selectedTemplate?.status),
        [selectedTemplate?.status]
    );

    return (
        <div className="row g-3 templates-layout">
            <div className="col-12 col-lg-3 templates-list-sidebar">
                <ClinicInfoPanel
                    title="Template types"
                    icon="bi-list-ul"
                    actions={
                        !listLoading && templates.length > 0 ? (
                            <span className="ecx-page-chip">
                                <i className="bi bi-telephone"></i>
                                {templates.length}
                            </span>
                        ) : null
                    }
                >
                    {listLoading ? (
                        <ClinicSettingsListSkeleton rows={5} />
                    ) : templates.length === 0 ? (
                        <ClinicSettingsEmpty
                            icon="bi-telephone"
                            title="No call templates"
                            description="No call templates are configured for this clinic."
                        />
                    ) : (
                        <TemplateTypeList
                            templates={templates}
                            selectedId={selectedId}
                            onSelect={loadTemplateById}
                            itemIcon="bi-telephone"
                        />
                    )}
                </ClinicInfoPanel>
            </div>

            <div className="col-12 col-lg-9">
                {!selectedId ? (
                    <ClinicSettingsEmpty
                        icon="bi-telephone"
                        title="Select a template"
                        description="Choose a template type from the list to view its call script."
                    />
                ) : detailLoading ? (
                    <ClinicInfoPanel title="Loading template…" icon="bi-telephone">
                        <ClinicSettingsListSkeleton rows={4} />
                    </ClinicInfoPanel>
                ) : (
                    <>
                        <div className="templates-readonly-banner">
                            <span className="templates-readonly-banner__icon">
                                <i className="bi bi-eye"></i>
                            </span>
                            <div>
                                <h6 className="templates-readonly-banner__title">View only</h6>
                                <p className="templates-readonly-banner__text mb-0">
                                    Call templates are managed by EyeconX. Contact support if you
                                    need changes to a call script.
                                </p>
                            </div>
                        </div>

                        <ClinicInfoPanel
                            title={formatReminderLabel(selectedTemplate?.reminderName) || 'Call template'}
                            icon="bi-telephone"
                        >
                            <div className="templates-detail-meta">
                                <ClinicInfoDetailItem label="Template name">
                                    {formatReminderLabel(selectedTemplate?.reminderName)}
                                </ClinicInfoDetailItem>

                                <div className="templates-detail-meta__status">
                                    <span className="templates-detail-meta__status-label">Status</span>
                                    <div className="templates-detail-meta__status-value">
                                        <span
                                            className={`clinic-settings-status-pill ${
                                                isActive ? 'is-open' : 'is-closed'
                                            }`}
                                        >
                                            {isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="templates-field-group">
                                <div className="templates-field-group__header">
                                    <label className="templates-field-group__label">
                                        Call script
                                    </label>
                                    <span className="templates-field-group__hint">
                                        {wordCount} words · {characterCount} characters
                                    </span>
                                </div>
                                <ClinicInfoDetailItem
                                    label="Script content"
                                    className="clinic-info-detail-item--full templates-readonly-content"
                                >
                                    {content || '—'}
                                </ClinicInfoDetailItem>
                                <div>
                                    <small className="text-muted d-block mb-2">
                                        Placeholders used in this script
                                    </small>
                                    <div className="templates-placeholder-chips">
                                        {PLACEHOLDERS.map((placeholder) => (
                                            <code key={placeholder}>{`{${placeholder}}`}</code>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {selectedTemplate?.audioPath && (
                                <div className="templates-field-group">
                                    <label className="templates-field-group__label">
                                        Audio preview
                                    </label>
                                    <div className="templates-audio-preview">
                                        {audioLoading ? (
                                            <span className="text-muted small">
                                                Loading audio…
                                            </span>
                                        ) : audioUrl ? (
                                            <audio controls src={audioUrl} preload="metadata" />
                                        ) : audioError ? (
                                            <span className="text-muted small">
                                                Audio preview is unavailable for this template.
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            )}

                            <div className="templates-detail-footer">
                                <span className="templates-detail-footer__updated">
                                    <i className="bi bi-clock"></i>
                                    Last updated {selectedTemplate?.modifiedAt || '—'}
                                </span>
                            </div>
                        </ClinicInfoPanel>
                    </>
                )}
            </div>
        </div>
    );
};

export default CallTemplates;
